import { Check } from '@mui/icons-material';
import {
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useState } from 'react';
import api from '~/api/Endpoints';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import { useHandleException } from '~/scripts/handleException';
const BENEFITS = [
  'Charged only for usage beyond your free monthly credits',
  'Billed at the end of each billing period',
  <>
    Same credit costs as the free tier — see{' '}
    <Link href="/api#pricing" preventScrollReset>
      pricing
    </Link>
  </>,
];

interface ApiSignupCardProps {
  loading: boolean;
  monthlyCredits: number;
}
export const ApiSignupCard = ({
  loading: loadingProp,
  monthlyCredits,
}: ApiSignupCardProps) => {
  const [loading, setLoading] = useState(false);
  const handleException = useHandleException();
  const addPaymentMethod = useCallback(async () => {
    try {
      setLoading(true);
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY!);
      const { id } = await api.post(`/app/checkout/save-card`, {
        body: {
          cancel_url: window.location.href,
          success_url: window.location.href,
        },
      });
      const result = await stripe!.redirectToCheckout({
        sessionId: id,
      });
      throw new Error(
        result?.error?.message || 'Error redirecting to checkout page'
      );
    } catch (error) {
      handleException(error as Error, { category: 'api addPayment' });
    } finally {
      setLoading(false);
    }
  }, [handleException]);

  return (
    <Paper variant="outlined" sx={{ p: 4, height: '100%', bgcolor: 'grey.50' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}>
        <Typography variant="h5">Pay-as-you-go</Typography>
        <Chip label="Not set up" size="small" variant="outlined" />
      </Stack>
      <Typography variant="h6" component="p" fontWeight={400} sx={{ mb: 2 }}>
        Need more than your free credits? Add a payment method and keep going
        after you hit {monthlyCredits.toLocaleString()} — no interruptions to
        your requests.
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        {BENEFITS.map((benefit, index) => {
          return (
            <ListItem key={index} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Check color="success" />
              </ListItemIcon>
              <ListItemText
                primary={benefit}
                slotProps={{ primary: { variant: 'body1' } }}
              />
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="contained"
        size="large"
        disableElevation
        loading={loading || loadingProp}
        onClick={() => addPaymentMethod()}
        color="primary">
        <span>Add payment method</span>
      </Button>
      <Typography
        variant="body2"
        color="text.secondary"
        display="block"
        sx={{ mt: 3 }}>
        By adding a payment method you agree to our API{' '}
        <Link href="/terms" target="_blank" underline="always">
          Terms of Service
        </Link>
        .
      </Typography>
    </Paper>
  );
};
